import type { DeveloperLogField } from './developer-log.ts';

const ERROR_FIELDS = ['name', 'message', 'code', 'status', 'details', 'hint'] as const;
const MAX_CAUSE_DEPTH = 8;

function readErrorField(error: object, key: string): unknown {
  try {
    return Reflect.get(error, key);
  } catch {
    // A throwing accessor must not prevent copying the rest of the failure.
    return '[unreadable error field]';
  }
}

function isDiagnosticValue(value: unknown): value is string | number | boolean {
  return typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean';
}

export function developerErrorEvidence(error: unknown): DeveloperLogField[] {
  const fields: DeveloperLogField[] = [];
  const stacks: DeveloperLogField[] = [];
  const visited = new Set<object>();
  let current = error;

  for (let depth = 0; ; depth += 1) {
    const label = depth === 0 ? 'Error' : `Cause ${depth}`;
    if (current === null || typeof current !== 'object') {
      fields.push({ label: `${label} message`, value: String(current) });
      break;
    }
    if (visited.has(current)) {
      fields.push({ label, value: '[circular cause]' });
      break;
    }
    if (depth === MAX_CAUSE_DEPTH) {
      fields.push({ label, value: '[cause chain truncated]' });
      break;
    }
    visited.add(current);

    // Errors and object rejections share the same evidence boundary. In particular,
    // Error subclasses can carry database codes and details beyond their message.
    const previousLength = fields.length;
    for (const key of ERROR_FIELDS) {
      const value = readErrorField(current, key);
      if (isDiagnosticValue(value)) fields.push({ label: `${label} ${key}`, value });
    }
    if (fields.length === previousLength) {
      fields.push({ label, value: '[object without diagnostic fields]' });
    }
    const stack = readErrorField(current, 'stack');
    if (typeof stack === 'string') stacks.push({ label: `${label} stack`, value: stack });
    const cause = readErrorField(current, 'cause');
    if (cause === undefined) break;
    current = cause;
  }

  // Preserve operation context and underlying failure metadata before verbose stacks
  // consume the report's size budget.
  return [...fields, ...stacks];
}
